import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { BadgeCheck } from "lucide-react";
import { Reveal, TrackCorner, EASE } from "./primitives";
import { useLanguage } from "../../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD =
  "w-full rounded-xl border border-kn-cyan/25 bg-kn-navy/60 px-4 py-3 text-sm text-white shadow-inner placeholder:text-white/30 focus:border-kn-cyan focus:outline-none transition-colors duration-300";

export const Registry = () => {
  const { t } = useLanguage();
  const R = t.registry;
  const [form, setForm] = useState({ name: "", email: "", role: R.roles[0], region: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await axios.post(`${API}/registry-interest`, form);
      setStatus("sent");
    } catch {
      setStatus("idle");
      setError(R.error);
    }
  };

  return (
    <section id="join" className="noise relative overflow-hidden bg-kn-navy py-20 lg:py-28" data-testid="registry-section">
      <div className="pointer-events-none absolute -right-24 -bottom-24 text-kn-cyan/15">
        <TrackCorner className="h-[300px] w-[300px] rotate-180" />
      </div>
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <p className="flex items-center gap-4 font-mono text-[11px] tracking-[0.32em] uppercase text-kn-cyan" data-testid="registry-eyebrow">
              <span className="h-px w-10 bg-kn-cyan/60" />
              {R.eyebrow}
            </p>
            <h2 className="mt-6 font-serif text-4xl italic leading-[1.12] text-white sm:text-5xl" data-testid="registry-heading">
              {R.h2}
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-white/60">
              {R.body}
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <AnimatePresence mode="wait">
              {status === "sent" ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: EASE }}
                  className="flex flex-col items-start gap-4 rounded-2xl border border-kn-cyan/25 bg-white/[0.04] p-10"
                  data-testid="registry-success"
                >
                  <BadgeCheck className="h-10 w-10 text-kn-cyan" strokeWidth={1} />
                  <h3 className="text-2xl font-semibold text-white">{R.successTitle}</h3>
                  <p className="text-sm leading-relaxed text-white/60">{R.successBody}</p>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  onSubmit={submit}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.5, ease: EASE }}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] p-8"
                  data-testid="registry-form"
                >
                  <div className="grid gap-4 sm:grid-cols-2">
                    <input required data-testid="registry-name" placeholder={R.ph.name} value={form.name} onChange={set("name")} className={FIELD} />
                    <input required type="email" data-testid="registry-email" placeholder={R.ph.email} value={form.email} onChange={set("email")} className={FIELD} />
                  </div>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <select
                      data-testid="registry-role"
                      value={form.role}
                      onChange={set("role")}
                      aria-label={R.roleLabel}
                      className={`${FIELD} appearance-none bg-kn-navy`}
                    >
                      {R.roles.map((r) => (
                        <option key={r} value={r} className="bg-kn-navy text-white">{r}</option>
                      ))}
                    </select>
                    <input required data-testid="registry-region" placeholder={R.ph.region} value={form.region} onChange={set("region")} className={FIELD} />
                  </div>
                  {error && <p className="mt-4 text-sm text-red-400" data-testid="registry-error">{error}</p>}
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    data-testid="registry-submit"
                    className="mt-6 w-full rounded-full bg-brand-gradient py-3.5 text-sm font-semibold text-white shadow-[0_10px_30px_-8px_rgba(46,197,230,0.4)] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_28px_rgba(46,197,230,0.3)] active:scale-[0.98] disabled:opacity-60"
                  >
                    {status === "sending" ? R.sending : R.submit}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
